/**
 * AddRosterMemberDialog.jsx
 *
 * Roster Unification P1 — single dialog for the three manual add flows:
 *   • mode="player"  → POST /api/teams/{team_id}/roster                (createRosterPlayer)
 *   • mode="leader"  → POST /api/invites/personal                      (createPersonalInvitation)
 *   • mode="parent"  → POST /api/invites/personal (invite-first)
 *                      or POST /api/teams/{team_id}/guardian-links     (link-existing fallback)
 *
 * Backend remains the source of truth for permissions; this component just
 * triggers the right endpoint and refreshes via the parent's `onCreated`
 * callback. No new endpoints, no new state outside the dialog.
 *
 * Used from:
 *   • TeamSettings.jsx → "Trupp & Medlemmar" section buttons (Spelare/Ledare/Förälder)
 *   • Team.jsx / Trupp page → primary "Lägg till" dropdown
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Badge } from '../ui/badge';
import { Loader2, UserPlus, ShieldCheck, Heart, Search, Check, Link2 } from 'lucide-react';
import { BirthDatePicker } from '../shared/BirthDatePicker';
import { submitInvitationBatch } from '../../lib/invitationFlow';
import {
  createRosterPlayer,
  createPersonalInvitation,
  createGuardianLink,
  getTeamRoster,
  getTeamParents,
} from '../../lib/api';

// Roles accepted by POST /api/invites/personal (see personal_invitations.VALID_ROLES).
// `team_manager` is NOT accepted by that endpoint today — invite as `coach`
// and have the org-admin promote them later if needed.
const LEADER_ROLE_OPTIONS = [
  { value: 'head_coach', label: 'Huvudtränare' },
  { value: 'assistant_coach', label: 'Assisterande tränare' },
  { value: 'coach', label: 'Tränare / Ledare' },
];

const POSITION_OPTIONS = ['Målvakt', 'Back', 'Mittfält', 'Anfall'];

const MODE_META = {
  player: { title: 'Lägg till spelare', icon: UserPlus,
            description: 'Skapar en spelarprofil på truppen. Konto/inloggning är frivilligt.' },
  leader: { title: 'Lägg till ledare',  icon: ShieldCheck,
            description: 'Skickar en personlig inbjudan via e-post.' },
  parent: { title: 'Lägg till förälder', icon: Heart,
            description: 'Bjud in en ny förälder eller koppla en befintlig förälder till en spelare.' },
};

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const initialPlayerForm = () => ({
  first_name: '', last_name: '', birthdate: '',
  jersey_number: '', position: '', child_email: '', phone: '',
});

const initialLeaderForm = () => ({ name: '', email: '', role: 'assistant_coach' });

const initialParentForm = () => ({
  flow: 'invite',         // 'invite' | 'link'
  name: '', email: '',
  selectedPlayerProfileIds: [],
  // link-existing
  selectedParentUserId: '',
  relation: 'parent',
  parentSearch: '',
  playerSearch: '',
});

export default function AddRosterMemberDialog(props) {
  return <AddRosterMemberForm key={`${props.teamId}:${props.mode}:${props.open}:${props.defaultPlayerProfileId || ''}`} {...props} />;
}

function AddRosterMemberForm({
  open, onOpenChange, teamId, mode = 'player', onCreated, defaultPlayerProfileId = null,
}) {
  const meta = MODE_META[mode] || MODE_META.player;
  const Icon = meta.icon;

  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(null);
  const [results, setResults] = useState(null);
  const operation = useRef(false);
  const lifecycle = useRef(null);
  useEffect(() => {
    const token = {};
    lifecycle.current = token;
    return () => { if (lifecycle.current === token) lifecycle.current = null; };
  }, []);
  const close = () => {
    if (operation.current || busy) return;
    if (results !== null) onCreated?.({ mode });
    onOpenChange(false);
  };
  const sendReviewed = async () => {
    if (operation.current || !review || results !== null) return;
    operation.current = true;
    setBusy(true);
    setResults([]);
    const token = lifecycle.current;
    const isCurrent = () => token !== null && lifecycle.current === token;
    try {
      const rows = await submitInvitationBatch(teamId, review,
        { createPersonalInvitation, createGuardianLink }, isCurrent, setResults);
      if (isCurrent()) setResults(rows);
    } finally {
      operation.current = false;
      if (isCurrent()) setBusy(false);
    }
  };
  const [playerForm, setPlayerForm] = useState(initialPlayerForm);
  const [leaderForm, setLeaderForm] = useState(initialLeaderForm);
  const [parentForm, setParentForm] = useState(() => ({
    ...initialParentForm(),
    selectedPlayerProfileIds: defaultPlayerProfileId ? [defaultPlayerProfileId] : [],
  }));

  // Parent-mode helper data
  const [roster, setRoster] = useState([]);
  const [parents, setParents] = useState([]);
  const [loadingHelpers, setLoadingHelpers] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPlayerForm(initialPlayerForm());
    setLeaderForm(initialLeaderForm());
    setParentForm({
      ...initialParentForm(),
      selectedPlayerProfileIds: defaultPlayerProfileId ? [defaultPlayerProfileId] : [],
    });
  }, [open, mode, defaultPlayerProfileId]);

  useEffect(() => {
    if (!open || mode !== 'parent' || !teamId) return;
    let cancelled = false;
    setLoadingHelpers(true);
    Promise.all([getTeamRoster(teamId), getTeamParents(teamId).catch(() => ({ data: [] }))])
      .then(([rRes, pRes]) => {
        if (cancelled) return;
        setRoster(rRes.data?.players || rRes.data?.data || rRes.data || []);
        setParents(pRes.data?.data || pRes.data || []);
      })
      .catch(() => { if (!cancelled) toast.error('Kunde inte ladda lagdata'); })
      .finally(() => { if (!cancelled) setLoadingHelpers(false); });
    return () => { cancelled = true; };
  }, [open, mode, teamId]);

  // ── PLAYER ─────────────────────────────────────────────────────────────
  const submitPlayer = async () => {
    const { first_name, last_name, birthdate } = playerForm;
    if (!first_name.trim() || !last_name.trim() || !birthdate) {
      toast.error('Förnamn, efternamn och födelsedatum krävs');
      return;
    }
    if (playerForm.child_email && !EMAIL_RX.test(playerForm.child_email)) {
      toast.error('Ogiltig e-postadress');
      return;
    }
    setBusy(true);
    try {
      await createRosterPlayer(teamId, {
        first_name: first_name.trim(),
        last_name: last_name.trim(),
        birthdate,
        jersey_number: playerForm.jersey_number ? parseInt(playerForm.jersey_number, 10) : null,
        position: playerForm.position || null,
        child_email: playerForm.child_email || null,
        phone: playerForm.phone || null,
      });
      toast.success('Spelare tillagd i truppen');
      onCreated?.({ mode: 'player' });
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte lägga till spelare');
    } finally {
      setBusy(false);
    }
  };

  // ── LEADER ─────────────────────────────────────────────────────────────
  const submitLeader = async () => {
    const { name, email, role } = leaderForm;
    if (!name.trim() || !email.trim()) {
      toast.error('Namn och e-post krävs');
      return;
    }
    if (!EMAIL_RX.test(email.trim())) {
      toast.error('Ogiltig e-postadress');
      return;
    }
    if (!['head_coach', 'assistant_coach', 'coach'].includes(role)) {
      toast.error('Ogiltig roll');
      return;
    }
    setReview({ kind: 'invite', name: name.trim(), email: email.trim().toLowerCase(), role,
      items: [{ key: 'leader', name: name.trim() }] });
  };

  // ── PARENT ─────────────────────────────────────────────────────────────
  const filteredRoster = useMemo(() => {
    const q = parentForm.playerSearch.trim().toLowerCase();
    if (!q) return roster.slice(0, 25);
    return roster.filter((p) => {
      const n = `${p.first_name || ''} ${p.last_name || ''} ${p.display_name || p.name || ''}`.toLowerCase();
      return n.includes(q);
    }).slice(0, 25);
  }, [roster, parentForm.playerSearch]);

  const filteredParents = useMemo(() => {
    const q = parentForm.parentSearch.trim().toLowerCase();
    if (!q) return parents.slice(0, 25);
    return parents.filter((p) => {
      const n = `${p.name || ''} ${p.email || ''}`.toLowerCase();
      return n.includes(q);
    }).slice(0, 25);
  }, [parents, parentForm.parentSearch]);

  const togglePlayer = (id) => {
    setParentForm((s) => {
      const has = s.selectedPlayerProfileIds.includes(id);
      return {
        ...s,
        selectedPlayerProfileIds: has
          ? s.selectedPlayerProfileIds.filter((x) => x !== id)
          : [...s.selectedPlayerProfileIds, id],
      };
    });
  };

  const parentSubmitDisabled = useMemo(() => {
    if (busy) return true;
    if (parentForm.selectedPlayerProfileIds.length === 0) return true;
    if (parentForm.flow === 'invite') {
      return !parentForm.name.trim() || !EMAIL_RX.test(parentForm.email.trim());
    }
    return !parentForm.selectedParentUserId;
  }, [parentForm, busy]);

  const submitParent = async () => {
    if (parentForm.selectedPlayerProfileIds.length === 0) {
      toast.error('Välj minst en spelare');
      return;
    }
    if (parentForm.flow === 'invite' && (!parentForm.name.trim() || !EMAIL_RX.test(parentForm.email.trim()))) {
      toast.error('Ange namn och giltig e-postadress'); return;
    }
    if (parentForm.flow === 'link' && !parentForm.selectedParentUserId) {
      toast.error('Välj en befintlig förälder eller ledare'); return;
    }
    const items = [...new Set(parentForm.selectedPlayerProfileIds)].map((id) => {
      const player = roster.find((p) => (p.id || p._id) === id);
      return { key: id, playerId: id, name: player?.display_name || player?.name
        || `${player?.first_name || ''} ${player?.last_name || ''}`.trim() || 'Spelare' };
    });
    const selected = parents.find((p) => (p.user_id || p.id) === parentForm.selectedParentUserId);
    setReview(parentForm.flow === 'invite'
      ? { kind: 'invite', role: 'parent', name: parentForm.name.trim(), email: parentForm.email.trim().toLowerCase(), items }
      : { kind: 'link', userId: parentForm.selectedParentUserId, name: selected?.name || selected?.email || 'Medlem',
          relation: parentForm.relation || 'parent', items });
  };

  // ── Render ─────────────────────────────────────────────────────────────
  const submit = mode === 'player' ? submitPlayer
                : mode === 'leader' ? submitLeader
                : submitParent;

  const submitDisabled = busy || (mode === 'parent' && parentSubmitDisabled);

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value) close(); }}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        onEscapeKeyDown={(event) => { if (operation.current || busy) event.preventDefault(); }}
        onInteractOutside={(event) => { if (operation.current || busy) event.preventDefault(); }}
        data-testid={`add-roster-${mode}-dialog`}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="w-5 h-5" />
            {meta.title}
          </DialogTitle>
          <DialogDescription>{meta.description}</DialogDescription>
        </DialogHeader>

        {!review && <>
        {/* MODE: PLAYER */}
        {mode === 'player' && (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="ar-first">Förnamn *</Label>
                <Input id="ar-first" data-testid="add-player-first-name"
                  value={playerForm.first_name}
                  onChange={(e) => setPlayerForm({ ...playerForm, first_name: e.target.value })}
                  placeholder="Förnamn" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ar-last">Efternamn *</Label>
                <Input id="ar-last" data-testid="add-player-last-name"
                  value={playerForm.last_name}
                  onChange={(e) => setPlayerForm({ ...playerForm, last_name: e.target.value })}
                  placeholder="Efternamn" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Födelsedatum *</Label>
              <BirthDatePicker
                value={playerForm.birthdate}
                onChange={(v) => setPlayerForm({ ...playerForm, birthdate: v })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="ar-jersey">Tröjnummer</Label>
                <Input id="ar-jersey" type="number" min="0"
                  data-testid="add-player-jersey"
                  value={playerForm.jersey_number}
                  onChange={(e) => setPlayerForm({ ...playerForm, jersey_number: e.target.value })}
                  placeholder="Nr" />
              </div>
              <div className="space-y-1">
                <Label>Position</Label>
                <Select
                  value={playerForm.position}
                  onValueChange={(v) => setPlayerForm({ ...playerForm, position: v })}
                >
                  <SelectTrigger data-testid="add-player-position">
                    <SelectValue placeholder="Välj position" />
                  </SelectTrigger>
                  <SelectContent>
                    {POSITION_OPTIONS.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="ar-email">E-post (valfritt)</Label>
              <Input id="ar-email" type="email"
                data-testid="add-player-email"
                value={playerForm.child_email}
                onChange={(e) => setPlayerForm({ ...playerForm, child_email: e.target.value })}
                placeholder="spelare@example.com" />
              <p className="text-xs text-muted-foreground">
                Spelaren kan vara med på truppen utan e-post/login.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="ar-phone">Telefon (valfritt)</Label>
              <Input id="ar-phone"
                data-testid="add-player-phone"
                value={playerForm.phone}
                onChange={(e) => setPlayerForm({ ...playerForm, phone: e.target.value })}
                placeholder="07X-XXX XX XX" />
            </div>
          </div>
        )}

        {/* MODE: LEADER */}
        {mode === 'leader' && (
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="al-name">Namn *</Label>
              <Input id="al-name" data-testid="add-leader-name"
                value={leaderForm.name}
                onChange={(e) => setLeaderForm({ ...leaderForm, name: e.target.value })}
                placeholder="För- och efternamn" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="al-email">E-post *</Label>
              <Input id="al-email" type="email" data-testid="add-leader-email"
                value={leaderForm.email}
                onChange={(e) => setLeaderForm({ ...leaderForm, email: e.target.value })}
                placeholder="ledare@example.com" />
            </div>
            <div className="space-y-1">
              <Label>Roll *</Label>
              <Select
                value={leaderForm.role}
                onValueChange={(v) => setLeaderForm({ ...leaderForm, role: v })}
              >
                <SelectTrigger data-testid="add-leader-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEADER_ROLE_OPTIONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                En personlig inbjudan skickas via e-post.
              </p>
            </div>
          </div>
        )}

        {/* MODE: PARENT */}
        {mode === 'parent' && (
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
            {/* Flow selector */}
            <div className="inline-flex rounded-md bg-muted p-1 text-xs" data-testid="parent-flow-tabs">
              <button
                type="button"
                data-testid="parent-flow-invite"
                onClick={() => setParentForm({ ...parentForm, flow: 'invite' })}
                className={`px-3 py-1 rounded ${parentForm.flow === 'invite' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`}
              >
                Bjud in ny förälder
              </button>
              <button
                type="button"
                data-testid="parent-flow-link"
                onClick={() => setParentForm({ ...parentForm, flow: 'link' })}
                className={`px-3 py-1 rounded ${parentForm.flow === 'link' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`}
              >
                Koppla befintlig
              </button>
            </div>

            {/* Player picker — always required */}
            <div className="space-y-2">
              <Label>Koppla till spelare * <span className="text-xs text-muted-foreground">({parentForm.selectedPlayerProfileIds.length} valda)</span></Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
                <Input
                  data-testid="parent-player-search"
                  className="pl-8"
                  value={parentForm.playerSearch}
                  onChange={(e) => setParentForm({ ...parentForm, playerSearch: e.target.value })}
                  placeholder="Sök spelare på namn…"
                />
              </div>
              <div className="border border-border rounded max-h-44 overflow-y-auto divide-y divide-border">
                {loadingHelpers && <div className="p-3 text-sm text-muted-foreground">Laddar…</div>}
                {!loadingHelpers && filteredRoster.length === 0 && (
                  <div className="p-3 text-sm text-muted-foreground">Inga spelare hittades</div>
                )}
                {filteredRoster.map((p) => {
                  const id = p.id || p._id;
                  const sel = parentForm.selectedPlayerProfileIds.includes(id);
                  const label = p.display_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.name || id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => togglePlayer(id)}
                      data-testid={`parent-player-option-${id}`}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-muted/40 ${sel ? 'bg-primary/10' : ''}`}
                    >
                      <span>{label}</span>
                      {sel ? <Check className="w-4 h-4 text-primary" /> : null}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Invite vs Link */}
            {parentForm.flow === 'invite' ? (
              <div className="space-y-3 border-t border-border pt-3">
                <div className="space-y-1">
                  <Label htmlFor="ap-name">Förälderns namn *</Label>
                  <Input id="ap-name" data-testid="add-parent-name"
                    value={parentForm.name}
                    onChange={(e) => setParentForm({ ...parentForm, name: e.target.value })}
                    placeholder="För- och efternamn" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="ap-email">E-post *</Label>
                  <Input id="ap-email" type="email" data-testid="add-parent-email"
                    value={parentForm.email}
                    onChange={(e) => setParentForm({ ...parentForm, email: e.target.value })}
                    placeholder="foralder@example.com" />
                  <p className="text-xs text-muted-foreground">
                    Föräldern får en personlig inbjudan och kan använda sitt befintliga konto eller skapa ett nytt.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-3 border-t border-border pt-3">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><Link2 className="w-4 h-4" /> Befintlig förälder eller ledare *</Label>
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
                    <Input
                      data-testid="link-parent-search"
                      className="pl-8"
                      value={parentForm.parentSearch}
                      onChange={(e) => setParentForm({ ...parentForm, parentSearch: e.target.value })}
                      placeholder="Sök förälder eller ledare…"
                    />
                  </div>
                  <div className="border border-border rounded max-h-40 overflow-y-auto divide-y divide-border">
                    {loadingHelpers && <div className="p-3 text-sm text-muted-foreground">Laddar…</div>}
                    {!loadingHelpers && filteredParents.length === 0 && (
                      <div className="p-3 text-sm text-muted-foreground">
                        Inga tillgängliga föräldrar eller ledare — bjud in via fliken &quot;Bjud in ny förälder&quot;.
                      </div>
                    )}
                    {filteredParents.map((p) => {
                      const id = p.user_id || p.id;
                      const sel = parentForm.selectedParentUserId === id;
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setParentForm({ ...parentForm, selectedParentUserId: id })}
                          data-testid={`link-parent-option-${id}`}
                          className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-muted/40 ${sel ? 'bg-primary/10' : ''}`}
                        >
                          <span>
                            {p.name || p.email || id}
                            {p.email && p.name ? <span className="text-muted-foreground ml-2">{p.email}</span> : null}
                          </span>
                          {sel ? <Check className="w-4 h-4 text-primary" /> : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>Relation</Label>
                  <Select
                    value={parentForm.relation}
                    onValueChange={(v) => setParentForm({ ...parentForm, relation: v })}
                  >
                    <SelectTrigger data-testid="link-parent-relation">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="parent">Förälder</SelectItem>
                      <SelectItem value="mother">Mamma</SelectItem>
                      <SelectItem value="father">Pappa</SelectItem>
                      <SelectItem value="guardian">Annan vårdnadshavare</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-muted-foreground">
                  Kontot måste redan vara medlem i laget. Även en ledare kan kopplas som förälder utan att ledarrollen ändras.
                </p>
              </div>
            )}
          </div>
        )}

        </>}
        {review && <div className="space-y-3 py-3 text-sm" data-testid="invitation-review">
          <h3 className="font-semibold">{results === null ? 'Kontrollera uppgifterna' : busy ? 'Behandlar…' : 'Resultat'}</h3>
          <p><strong>{review.name}</strong>{review.email && <span className="block break-all">{review.email}</span>}</p>
          {review.kind === 'invite' ? <p className="text-muted-foreground">
            Personen kan använda sitt befintliga konto eller skapa ett nytt. {review.role === 'parent'
              ? 'En inbjudan skickas per valt barn. En befintlig ledarroll behålls när barnkopplingen bekräftas.'
              : `Lagroll: ${LEADER_ROLE_OPTIONS.find((r) => r.value === review.role)?.label || review.role}.`}
          </p> : <p className="text-muted-foreground">Barnkopplingen läggs till på valt konto. Befintlig lagroll behålls. Inget mejl skickas.</p>}
          <ul className="space-y-2" aria-live="polite">
            {review.items.map((item) => {
              const result = results?.find((row) => row.key === item.key);
              return <li key={item.key} className="border border-border rounded p-3">
                <strong>{item.name}</strong>
                {results !== null && <p className={['failed', 'saved_unsent', 'unknown'].includes(result?.status) ? "text-[rgb(var(--tn-text-fbbf24))]" : 'text-muted-foreground'}>
                  {result?.message || 'Väntar på resultat…'}
                </p>}
              </li>;
            })}
          </ul>
          {results !== null && !busy && <p className="text-xs text-muted-foreground">Kontrollera resultatet för varje person eller barn innan du stänger.</p>}
        </div>}

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={busy} data-testid={`add-roster-${mode}-cancel`}>
            {results !== null ? 'Stäng' : 'Avbryt'}
          </Button>
          {review && results === null && <Button variant="ghost" onClick={() => setReview(null)}>Ändra uppgifter</Button>}
          {results === null && <Button onClick={review ? sendReviewed : submit} disabled={submitDisabled}
            data-testid={`add-roster-${mode}-submit`}>
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {review ? (review.kind === 'link' ? 'Bekräfta koppling' : 'Skicka inbjudan')
              : mode === 'player' ? 'Lägg till spelare' : 'Granska uppgifter'}
          </Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const ADD_ROSTER_MODES = ['player', 'leader', 'parent'];
